import { useState, useMemo, useCallback, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRoleAccess } from '../../users/hooks/useRoleAccess';
import { useQuoteContext } from '../context/useData';
import { Plus } from 'lucide-react';
import {
  getStatusColor,
  getStatusLabel,
  QuoteWithRelations,
  SearchBar,
  StatusFilters,
  SortableHeader,
  Pagination,
  LoadingState,
  ErrorState,
  EmptyState
  } from '@cleanpro/common-ui';
import QuoteTableRow from './QuoteRow';

/**
 * Quotes component displays a paginated, filterable, and searchable list of quotes.
 *
 * Features:
 * - Fetches and displays quotes with loading and error states.
 * - Allows searching by customer name, quote number, or line item description.
 * - Filters quotes by status.
 * - Supports pagination with customizable items per page.
 * - Displays quote details including quote number, customer, amount, status, and validity.
 * - Shows status with color-coded badges and icons.
 * - Indicates expired quotes.
 * - Allows navigation to quote details and quote creation (if permitted).
 *
 * @component
 * @returns {JSX.Element} The rendered Quotes table with filters and pagination.
 */
export default function Quotes() {
  const permissions = useRoleAccess();
  const navigate = useNavigate();
  const { quotes, loading, error } = useQuoteContext();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const ITEMS_PER_PAGE = 25;

  // Filter quotes
  const { filteredQuotes, filterCounts } = useMemo(() => {
    const normalizedSearch = deferredSearchTerm.trim().toLowerCase();
    const counts: Record<'all' | 'assessed' | 'pending' | 'quoted' | 'expired' | 'cancelled', number> = {
      all: 0,
      assessed: 0,
      pending: 0,
      quoted: 0,
      expired: 0,
      cancelled: 0,
    };

    const matches = quotes.filter((quote: QuoteWithRelations) => {
      const haystacks = [
        quote.property?.addressLine1 ?? '',
        quote.customer?.name ?? '',
        quote.quoteNumber ?? '',
        ...(quote.lineItems?.map(item => item.description ?? '') ?? []),
      ];

      const matchesSearch =
        normalizedSearch.length === 0 ||
        haystacks.some((value) => value.toLowerCase().includes(normalizedSearch));

      if (!matchesSearch) {
        return false;
      }

      counts.all += 1;
      if (counts[quote.status as keyof typeof counts] !== undefined) {
        counts[quote.status as keyof typeof counts] += 1;
      }

      return true;
    });

    const filtered = matches.filter((quote) =>
      statusFilter === 'all' || quote.status === statusFilter
    );

    return { filteredQuotes: filtered, filterCounts: counts };
  }, [quotes, deferredSearchTerm, statusFilter]);

  const sortedQuotes = useMemo(() => {
    return [...filteredQuotes].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'quote':
          aValue = a.quoteNumber ?? '';
          bValue = b.quoteNumber ?? '';
          break;
        case 'customer':
          aValue = a.customer?.name ?? '';
          bValue = b.customer?.name ?? '';
          break;
        case 'amount':
            aValue = a.totalAmount ?? '';
            bValue = b.totalAmount ?? '';
            break;
        case 'status':
          aValue = a.status ?? '';
          bValue = b.status ?? '';
          break;
        case 'valid_until':
          aValue = new Date(a.validUntil ?? a.createdAt).getTime();
          bValue = new Date(b.validUntil ?? b.createdAt).getTime();
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        default: // createdAt fallback
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredQuotes, sortColumn, sortDirection]);

  const totalQuotes = sortedQuotes.length;
  const totalPages = Math.ceil(totalQuotes / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentQuotes = sortedQuotes.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    // Quote status filters based on workflow insights
    const QUOTE_FILTERS = useMemo(() => [
      { 
        value: 'all', 
        label: 'All Quotes', 
        count: filterCounts.all 
      },
      { 
        value: 'assessed', 
        label: 'Assessed', 
        count: filterCounts.assessed 
      },
      { 
        value: 'pending', 
        label: 'Pending', 
        count: filterCounts.pending 
      },
      { 
        value: 'quoted', 
        label: 'Quoted', 
        count: filterCounts.quoted 
      },
      { 
        value: 'expired', 
        label: 'Expired', 
        count: filterCounts.expired 
      },
      { 
        value: 'cancelled', 
        label: 'Cancelled', 
        count: filterCounts.cancelled 
      }
    ], [filterCounts]);

    // Memoized event handlers for better performance
    const handleSearchChange = useCallback((value: string) => {
      setSearchTerm(value);
      setCurrentPage(1); // Reset to first page when searching
    }, []);
  
    const handleFilterChange = useCallback((value: string) => {
      setStatusFilter(value);
      setCurrentPage(1); // Reset to first page when filtering
    }, []);

    const handleQuoteClick = useCallback((quote: QuoteWithRelations) => {
      navigate(`/quotes/${quote.id}`);
    }, [navigate]);
  
    const handleCreateQuote = useCallback(() => {
      navigate('/quotes/create');
    }, [navigate]);
  
    const handleSort = useCallback((column: string) => {
      if (sortColumn === column) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    }, [sortColumn, sortDirection]);

  // Add a refreshQuotes function to reload the page or refetch data
  const refreshQuotes = useCallback(() => {
    window.location.reload();
  }, []);

  let content;
  if (loading) {
    content = <LoadingState message="Loading quotes..." size="lg" />;
  } else if (error) {
    content = (
      <ErrorState 
        error={error} 
        onRetry={refreshQuotes} 
        showRetry={true}
      />
    );
  } else {
    content = (
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Quotes</h1>
          <p className="text-gray-600 mt-1">Quotes are generated when customers submit a request or can be manually created</p>
        </div>
        {permissions.canCreateQuotes && (
          <button
            onClick={handleCreateQuote}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 hover:bg-indigo-700 transition-colors min-h-[44px] whitespace-nowrap"
          >
            <Plus className="h-4 w-4" />
            <span>New Quote</span>
          </button>
        )}
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col space-y-4">
          {/* Search Bar */}
          <SearchBar
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search quotes by name, email, phone, or address..."
          />

          {/* Quote Type Filters */}
          <StatusFilters
            filters={QUOTE_FILTERS}
            selectedFilter={statusFilter}
            onFilterChange={handleFilterChange}
          />
        </div>
      </div>

      {/* Quotes Table */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200">
        {currentQuotes.length === 0 ? (
          <EmptyState
            title="No quotes found"
            description={
              searchTerm || statusFilter !== 'all'
                ? "No quotes match your current filters. Try adjusting your search or filter criteria."
                : "No quotes have been created yet."
            }
            actionLabel={permissions.canCreateQuotes && !searchTerm && statusFilter === 'all' ? "Create First Quote" : undefined}
            onAction={permissions.canCreateQuotes && !searchTerm && statusFilter === 'all' ? handleCreateQuote : undefined}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader 
                      label="Quote"
                      column="quote"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader 
                      label="Customer"
                      column="customer"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader 
                      label="Amount"
                      column="amount"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader 
                      label="Status"
                      column="status"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader 
                      label="Valid Until"
                      column="valid_until"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader 
                      label="Created At"
                      column="createdAt"
                      currentColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentQuotes.map((quote) => (
                    <QuoteTableRow
                      key={quote.id}
                      quote={quote}
                      onClick={handleQuoteClick}
                    />
                  ))}
                </tbody>
              </table>
            </div>

                  {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalQuotes}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPage}
              />
          </>
        )}
      </div>

     {/* Quotes List - Mobile Card View */}
      <div className="md:hidden space-y-4">
        {currentQuotes.map((quote) => (
          <div
            key={quote.id}
            onClick={() => navigate(`/quotes/${quote.id}`)}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 active:bg-gray-50 transition-colors"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900">
                  {quote.quoteNumber}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {quote.customer?.name}
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-900">
                  ${Number(quote.totalAmount ?? 0).toFixed(2)}
                </div>
                {(quote.discountAmount ?? 0) > 0 && (
                  <div className="text-xs text-green-600">
                    -${(quote.discountAmount ?? 0).toFixed(2)}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-lg ${getStatusColor(quote.status, 'quote')}`}>
                  {getStatusLabel(quote.status, 'quote')}
                </span>
              </div>
              {/* <div className={`text-xs ${isExpired(quote.validUntil) ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                Valid: {DateUtils.formatDateLong(quote.validUntil)}
              </div> */}
            </div>
            
            <div className="mt-2 text-xs text-gray-400">
              {(quote.lineItems?.length ?? 0)} item{(quote.lineItems?.length ?? 0) !== 1 ? 's' : ''}
            </div>
          </div>
        ))}
        
      </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {content}
    </div>
  );
}